import React, { useState, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import { GradeConfig, PolymerGroup } from '@/constants/polymerGrades';

interface FairPriceGradeSelectionModalProps {
  polymer: PolymerGroup;
  grades: GradeConfig[];
  onExport: (selectedGrades: string[]) => void;
  onCancel: () => void;
  isGenerating: boolean;
}

const FairPriceGradeSelectionModal: React.FC<FairPriceGradeSelectionModalProps> = ({
  polymer,
  grades,
  onExport,
  onCancel,
  isGenerating,
}) => {
  const [selectedGrades, setSelectedGrades] = useState<Set<string>>(new Set(grades.map(g => g.key)));

  const allSelected = selectedGrades.size === grades.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedGrades(new Set());
    } else {
      setSelectedGrades(new Set(grades.map(g => g.key)));
    }
  };

  const handleToggleGrade = (gradeKey: string) => {
    const newSelected = new Set(selectedGrades);
    if (newSelected.has(gradeKey)) {
      newSelected.delete(gradeKey);
    } else {
      newSelected.add(gradeKey);
    }
    setSelectedGrades(newSelected);
  };

  const handleExport = () => {
    if (selectedGrades.size > 0) {
      onExport(Array.from(selectedGrades));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-rs-off-black border border-rs-jet-light rounded-lg shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-rs-jet-light p-4">
          <h2 className="text-lg font-semibold text-rs-off-white">
            Select {polymer} Grades
          </h2>
          <button
            onClick={onCancel}
            disabled={isGenerating}
            className="text-rs-off-white/60 hover:text-rs-off-white disabled:opacity-50 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {/* Select All / Deselect All */}
          <div className="mb-4 pb-4 border-b border-rs-jet-light">
            <button
              onClick={handleSelectAll}
              disabled={isGenerating}
              className="w-full text-left px-3 py-2 rounded hover:bg-rs-jet-light disabled:opacity-50 transition-colors text-rs-off-white font-medium"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Grade List */}
          <div className="space-y-2">
            {grades.map((grade) => (
              <label
                key={grade.key}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-rs-jet-light cursor-pointer transition-colors disabled:opacity-50"
              >
                <input
                  type="checkbox"
                  checked={selectedGrades.has(grade.key)}
                  onChange={() => handleToggleGrade(grade.key)}
                  disabled={isGenerating}
                  className="rounded cursor-pointer"
                />
                <span className="text-rs-off-white text-sm">{grade.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-rs-jet-light p-4 bg-rs-off-black/40">
          <span className="text-xs text-rs-off-white/60">
            {selectedGrades.size} of {grades.length} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={isGenerating}
              className="px-4 py-2 rounded text-sm font-medium bg-rs-jet text-rs-off-white hover:bg-rs-jet-light disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isGenerating || selectedGrades.size === 0}
              className="px-4 py-2 rounded text-sm font-medium bg-rs-green text-rs-black hover:bg-rs-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-rs-black border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Export Selected
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FairPriceGradeSelectionModal;
